import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
// Shared glass design tokens load FIRST so technical's own tokens.css
// (imported next) overrides the colliding values — keeping the current
// warm palette intact while the glass-only tokens (glass-*, accent,
// status-*, shadow-glass) become available for the glass re-skin.
import '@vollgas/shared/styles/tokens.css'
import './styles/tokens.css'
import './styles/global.css'

/**
 * Inline boot — set the <html> flags BEFORE first paint so we don't
 * flash the wrong palette / power mode. Mirrors the inline script in
 * the mockup's <head> (technical sets these from TS, not an index.html
 * script — that's this app's established pattern).
 */
function bootHtmlFlags() {
  try {
    let theme = localStorage.getItem('theme')
    if (!theme && window.matchMedia?.('(prefers-color-scheme: dark)').matches) theme = 'dark'
    if (theme === 'dark' || theme === 'light') {
      document.documentElement.setAttribute('data-theme', theme)
    }
  } catch { /* localStorage unavailable */ }

  // Technician hardware is consistently low-powered (older shop-floor
  // phones + tablets), so the glass UI always runs in lite mode:
  // data-power="low" strips backdrop-filter, gradient glows, and the
  // heavy composed shadows (see tokens.css + global.css). Forced on by
  // design — not a user-facing toggle. Make it one later by reading a
  // persisted value here, the way data-theme is read above.
  document.documentElement.setAttribute('data-power', 'low')
}

async function start() {
  bootHtmlFlags()

  const rootEl = document.getElementById('root')
  if (!rootEl) throw new Error('Root element #root not found')

  // DevProviders (MSW + DevPanel overlay) mount only when BOTH:
  //   - `import.meta.env.DEV` is true (so prod builds tree-shake it out), AND
  //   - `VITE_VOLLGAS_DEV_MODE === 'true'` (root /.env flag, propagated by
  //     scripts/sync-env.sh). Flipping the flag false hides the DevPanel
  //     button even when running `vite dev`.
  let devWrapper: ((node: React.ReactNode) => React.ReactNode) | undefined
  if (import.meta.env.DEV && import.meta.env.VITE_VOLLGAS_DEV_MODE === 'true') {
    const { DevProviders } = await import('./dev/DevProviders')
    devWrapper = (node) => <DevProviders>{node}</DevProviders>
  }

  createRoot(rootEl).render(
    <StrictMode>
      <App devWrapper={devWrapper} />
    </StrictMode>,
  )
}

void start()
